import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Publish as PublishIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const ExamsList = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, examId: null });
  const [expandedExam, setExpandedExam] = useState(null);

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/specific-exams/my-exams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExams(response.data);
    } catch (err) {
      setError('Error al cargar los exámenes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/specific-exams/${deleteDialog.examId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteDialog({ open: false, examId: null });
      loadExams();
    } catch (err) {
      setError('Error al eliminar el examen');
      console.error(err);
    }
  };

  const handlePublish = async (examId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/specific-exams/${examId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadExams();
    } catch (err) {
      setError('Error al publicar el examen');
      console.error(err);
    }
  };

  const handleToggleVisibility = async (exam) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = exam.is_public ? false : true;
      
      await axios.put(
        `${API_URL}/api/specific-exams/${exam.id}`,
        { is_public: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadExams();
    } catch (err) {
      setError('Error al cambiar visibilidad del examen');
      console.error(err);
    }
  };

  const toggleExpandExam = (examId) => {
    setExpandedExam(expandedExam === examId ? null : examId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'published':
        return '✓ Publicado';
      case 'draft':
        return '📝 Borrador';
      case 'archived':
        return '📦 Archivado';
      default:
        return status;
    }
  };

  const getAreaLabel = (area) => {
    const areas = {
      matematicas: 'Matemáticas',
      'lectura-critica': 'Lectura Crítica',
      'lectura_critica': 'Lectura Crítica',
      sociales: 'Sociales y Ciudadanas',
      ciencias: 'Ciencias Naturales',
      ingles: 'Inglés',
      todas: 'Todas las Áreas',
    };
    return areas[area] || area;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/teacher')}
            sx={{ mb: 2 }}
          >
            Volver al Panel
          </Button>
          <Typography variant="h4" gutterBottom>
            Mis Exámenes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/teacher/exams/create')}
        >
          Crear Nuevo Examen
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {exams.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No has creado ningún examen todavía
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/teacher/exams/create')}
            sx={{ mt: 2 }}
          >
            Crear Mi Primer Examen
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Área</TableCell>
                <TableCell align="center">Preguntas</TableCell>
                <TableCell align="center">Tiempo (min)</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Visible</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exams.map((exam) => (
                <React.Fragment key={exam.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpandExam(exam.id)}
                      >
                        {expandedExam === exam.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {exam.title}
                      </Typography>
                      {exam.description && (
                        <Typography variant="caption" color="text.secondary">
                          {exam.description.substring(0, 80)}
                          {exam.description.length > 80 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={getAreaLabel(exam.area)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={exam.question_count || exam.questions?.length || 0} 
                        size="small" 
                        color="primary" 
                      />
                    </TableCell>
                    <TableCell align="center">{exam.time_limit || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(exam.status)}
                        size="small"
                        color={getStatusColor(exam.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={exam.is_public ? "Ocultar examen" : "Hacer visible"}>
                        <Switch
                          checked={exam.is_public}
                          onChange={() => handleToggleVisibility(exam)}
                          color="success"
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/teacher/exams/edit/${exam.id}`)}
                        title="Editar examen"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {exam.status === 'draft' && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handlePublish(exam.id)}
                          title="Publicar examen"
                        >
                          <PublishIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, examId: exam.id })}
                        title="Eliminar examen"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                      <Collapse in={expandedExam === exam.id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Preguntas del Examen ({exam.questions?.length || 0})
                          </Typography>
                          {exam.questions && exam.questions.length > 0 ? (
                            <List dense>
                              {exam.questions.map((question, index) => (
                                <ListItem key={question.id}>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                          {index + 1}.
                                        </Typography>
                                        <Typography variant="body2">
                                          {question.question.substring(0, 100)}
                                          {question.question.length > 100 ? '...' : ''}
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                        <Chip 
                                          label={question.topic || 'Sin tema'} 
                                          size="small" 
                                          variant="outlined"
                                        />
                                        <Chip 
                                          label={question.difficulty === 'easy' ? 'Fácil' : 
                                                question.difficulty === 'medium' ? 'Medio' : 'Difícil'} 
                                          size="small" 
                                          color={
                                            question.difficulty === 'easy' ? 'success' : 
                                            question.difficulty === 'medium' ? 'warning' : 'error'
                                          }
                                        />
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Este examen no tiene preguntas asignadas todavía.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog de confirmación para eliminar */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, examId: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar este examen? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, examId: null })}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ExamsList;
